<template>
    <motion.div
        v-if="enabled"
        class="overflow-hidden -mb-2"
        :animate="{ height: showContainer ? 'auto' : 0 }"
        :transition="{ duration: 0.2, ease: 'easeOut' }"
    >
        <div
            class="relative w-full rounded-t-lg border-b-0 rounded-b-none bg-violet-50 border border-solid border-violet-100 px-3 py-2.5 pb-3.5"
            @mouseenter="hovered = true"
            @mouseleave="hovered = false"
        >
            <div class="absolute left-3 bottom-4.5 flex items-center">
                <font-awesome-icon :icon="faSparkles" class="text-violet-500 text-xs shrink-0" />
            </div>
            <div v-if="suggestedReply === undefined" class="h-[1.2rem] flex items-center pl-5 pb-1">
                <div class="animate-pulse bg-violet-200 rounded-sm h-3 w-full" />
            </div>
            <template v-else-if="suggestedReply">
                <motion.div
                    class="overflow-hidden"
                    :animate="{ height: hovered ? 'auto' : '1.2rem' }"
                    :transition="{ duration: 0.2, ease: 'easeOut' }"
                >
                    <p ref="textElement" class="text-xs text-violet-900 m-0 whitespace-pre-line pl-5 pr-[7.5rem]">
                        {{ suggestedReply }}
                    </p>
                </motion.div>
                <div class="absolute right-3 bottom-3.5 flex items-center">
                    <vx-button
                        data-pendo="message-hub-use-suggestion"
                        outline
                        color="violet"
                        size="xs"
                        class="shrink-0"
                        @click.stop="useSuggestedReply"
                    >
                        Use Suggestion
                    </vx-button>
                </div>
                <div
                    v-if="overflows"
                    class="absolute inset-be-3.5 inset-s-0 inset-e-0 flex justify-center pointer-events-none transition-opacity duration-200 ease-out"
                    :class="hovered ? 'opacity-0' : 'opacity-100'"
                >
                    <font-awesome-icon :icon="faEllipsis" class="text-violet-500 text-xs translate-y-1/2" />
                </div>
            </template>
        </div>
    </motion.div>
</template>

<script lang="ts" setup>
import { faEllipsis, faSparkles } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import { VxButton } from '@voxie/frontend-components';
import { computed, inject, nextTick, onBeforeUnmount, ref, watch } from 'vue';
import { motion } from 'motion-v';
import { useIntelligentInbox } from '~/components/message-hub/intelligent-inbox/useIntelligentInbox';
import { canUseIntelligentInbox, isSameThread } from '~/components/message-hub/messageHubUtil';
import { useConversation } from '~/composables/useConversation';
import { useCurrentGroup } from '~/composables/useCurrentGroup';
import type { Features } from '~/provides/features.types';

const props = defineProps<{
    focusTextarea?: (options?: { cursorAtEnd?: boolean }) => void | Promise<void>;
}>();

const FEATURES = inject('FEATURES') as Features;

const { getContext } = useCurrentGroup();
const { activeThread, message } = useConversation();
const { fetchSuggestion, invalidateSuggestion } = useIntelligentInbox();

const hovered = ref(false);
const textElement = ref<HTMLElement | null>(null);
const overflows = ref(false);
const suggestedReply = ref<string | undefined>('');

const enabled = computed(() => {
    return !!activeThread.value && canUseIntelligentInbox(FEATURES) && FEATURES.message_hub_ai;
});

const conversationKey = computed(() => {
    if (!enabled.value) {
        return '';
    }
    return `${activeThread.value.group_id ?? ''}:${activeThread.value.contact_id}`;
});

const showContainer = computed(() => {
    return suggestedReply.value === undefined || suggestedReply.value !== '';
});

watch(suggestedReply, async () => {
    await nextTick();
    const element = textElement.value;
    overflows.value = !!element && element.scrollHeight > 20;
});

watch(
    conversationKey,
    async (key, _previousKey, onCleanup) => {
        hovered.value = false;

        if (key === '') {
            suggestedReply.value = '';
            overflows.value = false;
            return;
        }

        suggestedReply.value = undefined;

        let cancelled = false;
        onCleanup(() => {
            cancelled = true;
        });

        if (activeThread.value.status === 'closed' && !FEATURES.simple_message_hub) {
            suggestedReply.value = '';
            overflows.value = false;
            return;
        }

        const groupId = activeThread.value.group_id ?? '';
        const contactId = activeThread.value.contact_id;
        const reply = await fetchSuggestion(groupId, contactId);
        if (!cancelled) {
            suggestedReply.value = reply;
        }
    },
    { immediate: true }
);

const useSuggestedReply = () => {
    if (suggestedReply.value) {
        message.body = suggestedReply.value;
        props.focusTextarea?.({ cursorAtEnd: true });
    }
};

const handleMessageCreated = ({
    message: echoMessage,
}: {
    message?: { contact_id: number; group_id?: string; direction?: string };
}): void => {
    if (suggestedReply.value === undefined) {
        return;
    }

    if (!enabled.value || !echoMessage) {
        return;
    }
    if (!isSameThread(activeThread.value, echoMessage)) {
        return;
    }

    const groupId = activeThread.value.group_id ?? '';
    const contactId = activeThread.value.contact_id;

    invalidateSuggestion(groupId, contactId);

    if (echoMessage.direction === 'outbound' || activeThread.value.status === 'closed') {
        suggestedReply.value = '';
        overflows.value = false;
        return;
    }

    suggestedReply.value = undefined;

    fetchSuggestion(groupId, contactId).then((reply) => {
        suggestedReply.value = reply;
    });
};

const { teamId, orgUnitId, groupId } = getContext();
const channelName =
    !orgUnitId && !groupId
        ? `messages.${teamId}`
        : orgUnitId
        ? `messages.${teamId}.org_unit.${orgUnitId}`
        : `messages.${teamId}.${groupId}`;
const echoChannel: EchoChannel = window.Echo.private(channelName);
echoChannel.listen('MessageCreated', handleMessageCreated);

onBeforeUnmount(() => {
    echoChannel.stopListening('MessageCreated', handleMessageCreated);
});
</script>
